import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Tab, TabList, Text } from '../../ui/index.js'
import { type TaskListPage, type TaskListItem } from '../manager/useTasks.js'

type ProgressTab = 'approved' | 'awaiting' | 'improving'

const ACTIVE_STATUSES = 'ASSIGNED,IN_PROGRESS,RETURNED'

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso))
}

function taskQuery(statuses: string): string {
  return `/api/tasks?statuses=${encodeURIComponent(statuses)}`
}

function taskRows(items: readonly TaskListItem[]): readonly TaskListItem[] {
  return [...items].sort((a, b) => Date.parse(a.effectiveDeadlineAt) - Date.parse(b.effectiveDeadlineAt))
}

function uniqueById(items: readonly TaskListItem[]): TaskListItem[] {
  const seen = new Set<string>()
  const rows: TaskListItem[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    rows.push(item)
  }
  return rows
}

function tableShell(children: ReactNode) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {children}
    </section>
  )
}

function TaskTable({
  rows,
  showClosedDate,
}: {
  rows: readonly TaskListItem[]
  showClosedDate: boolean
}) {
  return tableShell(
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Reference</th>
          <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Title</th>
          <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Complexity</th>
          {showClosedDate ? <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Closed date</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontFamily: 'var(--font-mono)' }}>{row.reference}</td>
            <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontWeight: 500 }}>{row.title}</td>
            <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{row.complexity} / 10</td>
            {showClosedDate ? <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(row.closedAt)}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>,
  )
}

function NextActionList({ rows }: { rows: readonly TaskListItem[] }) {
  if (rows.length === 0) {
    return <EmptyState title="Nothing to act on" hint="Nothing is overdue and nothing is waiting on corrections — keep going." />
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
      {rows.map((row) => (
        <Link
          key={row.id}
          to={`/my-day?task=${encodeURIComponent(row.id)}`}
          style={{
            display: 'grid',
            gap: 4,
            textDecoration: 'none',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--o-space-3)',
            color: 'inherit',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
            <span style={{ fontWeight: 600 }}>{row.title}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-muted)' }}>{row.reference}</span>
          </div>
          <Text color="secondary">
            {row.isOverdue ? 'Overdue' : row.status === 'RETURNED' ? 'Returned for correction' : 'Next deadline'}
          </Text>
        </Link>
      ))}
    </div>
  )
}

export function MyProgress() {
  usePageTitle('My Progress')
  const { api } = useAuth()
  const [tab, setTab] = useState<ProgressTab>('approved')

  const approvedQuery = useQuery({
    queryKey: ['tasks', 'APPROVED'],
    queryFn: () => api.get<TaskListPage>(taskQuery('APPROVED')),
    retry: false,
  })
  const awaitingQuery = useQuery({
    queryKey: ['tasks', 'SUBMITTED'],
    queryFn: () => api.get<TaskListPage>(taskQuery('SUBMITTED')),
    retry: false,
  })
  const activeQuery = useQuery({
    queryKey: ['tasks', ACTIVE_STATUSES],
    queryFn: () => api.get<TaskListPage>(taskQuery(ACTIVE_STATUSES)),
    retry: false,
  })

  const loading = approvedQuery.isPending || awaitingQuery.isPending || activeQuery.isPending
  const failure = approvedQuery.error ?? awaitingQuery.error ?? activeQuery.error
  const hasFailure = approvedQuery.isError || awaitingQuery.isError || activeQuery.isError

  if (loading) {
    return (
      <>
        <Topbar title="My progress" eyebrow="Employee workspace" />
        <div className="page">
          <div className="page-section">
            <LoadingState label="Loading your progress" />
          </div>
        </div>
      </>
    )
  }

  if (hasFailure) {
    return (
      <>
        <Topbar title="My progress" eyebrow="Employee workspace" />
        <div className="page">
          <div className="page-section">
            <ErrorState error={failure} onRetry={() => void Promise.all([approvedQuery.refetch(), awaitingQuery.refetch(), activeQuery.refetch()])} />
          </div>
        </div>
      </>
    )
  }

  const approvedRows = taskRows((approvedQuery.data?.items ?? []) as readonly TaskListItem[])
  const awaitingRows = taskRows((awaitingQuery.data?.items ?? []) as readonly TaskListItem[])
  const activeRows = taskRows((activeQuery.data?.items ?? []) as readonly TaskListItem[])
  const returns = activeRows.filter((task) => task.status === 'RETURNED').length
  const overdue = activeRows.filter((task) => task.isOverdue).length
  const nearestActive = [...activeRows]
    .filter((task) => task.status === 'ASSIGNED' || task.status === 'IN_PROGRESS')
    .sort((a, b) => Date.parse(a.effectiveDeadlineAt) - Date.parse(b.effectiveDeadlineAt))[0]
  const nextActionRows = uniqueById([
    ...activeRows.filter((task) => task.isOverdue),
    ...activeRows.filter((task) => task.status === 'RETURNED'),
    ...(nearestActive === undefined ? [] : [nearestActive]),
  ])

  // A tie is not the same as having neither. Saying "nothing is overdue" to someone with
  // two overdue tasks is worse than saying nothing at all, so the all-clear is reserved
  // for the case where both counts are genuinely zero.
  const improvementSummary =
    returns === 0 && overdue === 0
      ? 'Nothing is overdue and nothing is waiting on corrections — keep going.'
      : returns > overdue
        ? 'Most of the work coming back to you is returns for corrections rather than missed deadlines — slowing down for a self-check before submitting will help most.'
        : overdue > returns
          ? 'Most of the work coming back to you is missed deadlines rather than returns for corrections — focusing on the deadline before you submit will help most.'
          : 'Deadlines and corrections are costing you about the same right now — the list below is the place to start.'

  return (
    <>
      <Topbar title="My progress" eyebrow="Employee workspace" />

      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
          <TabList activeTabId={tab} onTabChange={(id) => setTab(id as ProgressTab)}>
            <Tab id="approved">Approved</Tab>
            <Tab id="awaiting">Awaiting review</Tab>
            <Tab id="improving">Improving</Tab>
          </TabList>

          {tab === 'approved' ? (
            approvedRows.length === 0 ? (
              <EmptyState title="No approved tasks yet" hint="Approved work will appear here once it is closed." />
            ) : (
              <TaskTable rows={approvedRows} showClosedDate />
            )
          ) : null}

          {tab === 'awaiting' ? (
            awaitingRows.length === 0 ? (
              <EmptyState title="Nothing awaiting review" hint="Submitted work will appear here while your manager reviews it." />
            ) : (
              <TaskTable rows={awaitingRows} showClosedDate={false} />
            )
          ) : null}

          {tab === 'improving' ? (
            <div style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
              <section
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  padding: 'var(--o-space-4)',
                }}
              >
                <div className="card-label">Which half costs more</div>
                <Text>{improvementSummary}</Text>
              </section>

              <section
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  padding: 'var(--o-space-4)',
                }}
              >
                <div className="card-label">Next actions</div>
                <div style={{ marginTop: 'var(--o-space-3)' }}>
                  <NextActionList rows={nextActionRows} />
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
